import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TextInputProps,
  StyleSheet,
  Platform,
  Dimensions,
} from 'react-native';
import { colors, typography } from '../../theme';
import { designTokens, componentStyles, createComponentStyle } from '../../theme/design-system';
import { useLocale } from '../../contexts/LocaleContext';
import { useTypography } from '../../hooks/useTypography';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface InputProps extends Omit<TextInputProps, 'style'> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'filled';
  state?: 'default' | 'focused' | 'error' | 'disabled';
  containerStyle?: any;
  labelStyle?: any;
  inputStyle?: any;
  errorStyle?: any;
  helperStyle?: any;
  /** Force Panton/Latin font regardless of locale — use for email, phone, reference inputs */
  latin?: boolean;
}

export const Input = React.forwardRef<TextInput, InputProps>(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  size = 'md',
  variant = 'default',
  state = 'default',
  containerStyle,
  labelStyle,
  inputStyle,
  errorStyle,
  helperStyle,
  latin = false,
  onFocus,
  onBlur,
  editable = true,
  placeholder: placeholderProp,
  secureTextEntry,
  ...props
}, ref) => {
  const styles = useThemedStyles(getStyles);
  const [isFocused, setIsFocused] = useState(false);
  const [screenWidth] = useState(Dimensions.get('window').width);
  const { isRTL } = useLocale();
  const typo = useTypography();
  // Detect if current value contains Arabic characters
  const hasArabicChars = (text?: string) => /[\u0600-\u06FF]/.test(text || '');

  // Always-latin fields (by type) or explicit latin prop
  const forceLatinFont = latin
    || props.keyboardType === 'email-address'
    || props.keyboardType === 'phone-pad'
    || props.keyboardType === 'numeric'
    || props.keyboardType === 'number-pad'
    || props.autoComplete === 'email'
    || props.autoComplete === 'tel'
    || secureTextEntry === true;

  // Use latin font when: forced, or locale is RTL but value is Latin text
  const valueIsArabic = hasArabicChars(props.value as string);
  const isLatinInput = forceLatinFont || (isRTL && !valueIsArabic && !!props.value);
  const activeFont = isLatinInput ? typo.latinFontFamily : typo.fontFamily;

  // On native, use custom placeholder overlay so we can apply thin font independently of input font
  const useCustomPlaceholder = Platform.OS !== 'web' && isRTL;
  const isEmpty = !props.value && !props.defaultValue;

  // Determine current state
  const currentState = !editable ? 'disabled' : error ? 'error' : isFocused ? 'focused' : state;

  // Create styles using design system
  const inputStyles = createComponentStyle(
    componentStyles.input,
    variant,
    size,
    currentState
  );

  const handleFocus = (e: any) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  // Responsive sizing
  const getResponsiveHeight = () => {
    if (screenWidth >= designTokens.breakpoints.desktop) {
      return designTokens.sizes.input[size]; // Use the updated sizes
    }
    return designTokens.sizes.input[size];
  };

  return (
    <View style={[styles.container, containerStyle]}>
      {/* Label */}
      {label && (
        <Text style={[styles.label, { fontFamily: typo.fontFamily.medium, textAlign: isRTL ? 'right' : 'left' }, labelStyle]}>
          {label}
        </Text>
      )}

      {/* Input Container */}
      <View style={styles.inputContainer}>
        {/* Left Icon */}
        {leftIcon && (
          <View style={styles.leftIcon}>
            {leftIcon}
          </View>
        )}

        {/* Text Input */}
        <TextInput
          ref={ref}
          style={[
            styles.input,
            inputStyles,
            {
              height: getResponsiveHeight(),
              paddingLeft: leftIcon ? designTokens.spacing.xl : designTokens.spacing.md,
              paddingRight: rightIcon ? designTokens.spacing.xl : designTokens.spacing.md,
              fontFamily: activeFont.primary,
              fontWeight: '400',
              textAlign: isRTL ? 'right' : 'left',
            },
            inputStyle,
          ]}
          placeholder={useCustomPlaceholder ? '' : placeholderProp}
          placeholderTextColor={colors.textLight}
          secureTextEntry={secureTextEntry}
          editable={editable}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...props}
        />

        {/* Custom thin placeholder overlay for native RTL */}
        {useCustomPlaceholder && isEmpty && !isFocused && placeholderProp && (
          <Text
            pointerEvents="none"
            style={[
              styles.input,
              inputStyles,
              styles.customPlaceholder,
              {
                height: getResponsiveHeight(),
                paddingLeft: leftIcon ? designTokens.spacing.xl : designTokens.spacing.md,
                paddingRight: rightIcon ? designTokens.spacing.xl : designTokens.spacing.md,
                fontFamily: typo.fontFamily.thin,
                textAlign: 'right',
              },
            ]}
          >
            {placeholderProp}
          </Text>
        )}

        {/* Right Icon */}
        {rightIcon && (
          <View style={styles.rightIcon}>
            {rightIcon}
          </View>
        )}
      </View>

      {/* Helper Text */}
      {helperText && !error && (
        <Text style={[styles.helperText, { fontFamily: typo.fontFamily.primary, textAlign: isRTL ? 'right' : 'left' }, helperStyle]}>
          {helperText}
        </Text>
      )}

      {/* Error Text */}
      {error && (
        <Text style={[styles.errorText, { fontFamily: typo.fontFamily.primary, textAlign: isRTL ? 'right' : 'left' }, errorStyle]}>
          {error}
        </Text>
      )}
    </View>
  );
});

const getStyles = () => StyleSheet.create({
  container: {
    marginBottom: designTokens.spacing.md,
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500', // medium
    color: colors.textDark,
    marginBottom: designTokens.spacing.xs,
  },
  inputContainer: {
    position: 'relative',
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    backgroundColor: colors.background.surface,
    // Web-specific adjustments
    ...(Platform.OS === 'web' && {
      maxWidth: '100%',
      minWidth: 200,
    }),
  },
  customPlaceholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    color: colors.textLight,
    fontSize: typography.fontSize.sm,
    borderWidth: 0,
    backgroundColor: 'transparent',
    pointerEvents: 'none',
  },
  leftIcon: {
    position: 'absolute',
    left: designTokens.spacing.sm,
    top: '50%',
    transform: [{ translateY: -10 }],
    zIndex: designTokens.zIndex.base + 1,
  },
  rightIcon: {
    position: 'absolute',
    right: designTokens.spacing.sm,
    top: '50%',
    transform: [{ translateY: -10 }],
    zIndex: designTokens.zIndex.base + 1,
  },
  helperText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    marginTop: designTokens.spacing.xs,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    marginTop: designTokens.spacing.xs,
  },
});
